import { useCallback, useEffect, useRef, useState } from "react";
import type { CosmeticLoadout, MatchMode } from "@craftoff/game-core";
import { MATCH_BINARY_SUBPROTOCOL, PROTOCOL_VERSION, QUEUE_TICKET_TTL_MS } from "@craftoff/protocol";
import { leaveMatchmaking, type MatchmakingResult } from "./account-api";
import { clientError, clientLog } from "./client-log";
import { webSocketUrl } from "./platform";

const CANCELLATION_ACK_TIMEOUT_MS = 1_500;

export type MatchmakingQueueState = { mode: MatchMode; queueName: string | null; ticketId: string; joinedAt: number; error: string | null };
export type MatchOffer = {
  offerId: string;
  ticketId: string;
  expiresAt: number;
  region: string;
  opponentUsername: string;
  opponentXp: number;
  opponentCosmetics: CosmeticLoadout;
  opponentSpecialItemId: string | null;
};

type MatchedResult = Extract<MatchmakingResult, { status: "matched" }>;
type ServerMessage = {
  type?: string;
  protocolVersion?: number;
  ticketId?: string;
  serverTime?: number;
  joinedAt?: number;
  queueName?: string;
  offerId?: string;
  expiresAt?: number;
  region?: string;
  matchId?: string;
  token?: string;
  opponentId?: string;
  opponentXp?: number;
  opponentRating?: number;
  opponent?: { username?: string; xp?: number; cosmetics?: CosmeticLoadout; equippedSpecialItemId?: string | null };
  code?: string;
  terminal?: boolean;
  retryAfterMs?: number;
  ticketExpiresAt?: number;
};
type PendingCancellation = {
  promise: Promise<void>;
  resolve: () => void;
  timer: number | null;
  settled: boolean;
  failureEvent: string;
};
type Session = {
  mode: MatchMode;
  serverMode: "casual" | "ranked";
  region: string;
  ticketId: string;
  queueName: string | null;
  joinedAt: number;
  intentionalClose: boolean;
  connectedMatchId: string | null;
  connectionAttempt: number;
  reconnectDelay: number;
  retryAfterMs: number;
  ticketExpiresAt: number;
  equippedSpecialItemId: string | null;
  socket: WebSocket | null;
  cancellation: PendingCancellation | null;
  httpCancellation: Promise<void> | null;
};

export function useMatchmaking(callbacks: {
  onState: (state: MatchmakingQueueState | null) => void;
  onMatched: (match: MatchedResult, mode: MatchMode, socket: WebSocket) => void;
}) {
  const callbackRef = useRef(callbacks);
  const socketRef = useRef<WebSocket | null>(null);
  const sessionRef = useRef<Session | null>(null);
  const reconnectTimer = useRef<number | null>(null);
  const pendingCancellationRef = useRef<Promise<void> | null>(null);
  const [offer, setOffer] = useState<MatchOffer | null>(null);

  useEffect(() => { callbackRef.current = callbacks; }, [callbacks]);

  const stateFor = useCallback((session: Session, error: string | null = null): MatchmakingQueueState => ({
    mode: session.mode,
    queueName: session.queueName,
    ticketId: session.ticketId,
    joinedAt: session.joinedAt,
    error
  }), []);

  const finishMatch = useCallback((session: Session, message: ServerMessage | MatchedResult) => {
    if (!("matchId" in message) || !message.matchId || session.connectedMatchId === message.matchId) return;
    session.connectedMatchId = message.matchId;
    session.intentionalClose = true;
    if (sessionRef.current === session) sessionRef.current = null;
    setOffer(null);
    const socket = session.socket;
    if (!socket) return;
    const nestedOpponent = "opponent" in message ? message.opponent : undefined;
    const match: MatchedResult = {
      status: "matched",
      queueName: session.queueName ?? ("queueName" in message ? message.queueName : "") ?? "",
      matchId: message.matchId,
      opponentId: ("opponentId" in message ? message.opponentId : "") ?? "",
      opponentUsername: ("opponentUsername" in message ? message.opponentUsername : nestedOpponent?.username) ?? "PLAYER",
      opponentXp: ("opponentXp" in message ? message.opponentXp : nestedOpponent?.xp) ?? 0,
      opponentRating: ("opponentRating" in message ? message.opponentRating : 0) ?? 0,
      opponentCosmetics: ("opponentCosmetics" in message ? message.opponentCosmetics : nestedOpponent?.cosmetics) ?? { wallpaperId: "overworld-day", craftingTableId: "classic-brown", faceplateId: "trailblazer" },
      opponentSpecialItemId: nestedOpponent?.equippedSpecialItemId ?? null,
      matchedAt: ("serverTime" in message ? message.serverTime : Date.now()) ?? Date.now(),
      token: ("token" in message ? message.token : "") ?? ""
    };
    callbackRef.current.onMatched(match, session.mode, socket);
  }, []);

  const connectRef = useRef<(session: Session) => void>(() => undefined);

  const finishTerminal = useCallback((session: Session, code: string) => {
    setOffer(null);
    session.intentionalClose = true;
    callbackRef.current.onState(stateFor(session, code));
    session.socket?.close(1000, "Queue ended");
  }, [stateFor]);

  const recover = useCallback((session: Session) => {
    if (session.intentionalClose || session.connectedMatchId) return;
    const now = Date.now();
    if (now >= session.ticketExpiresAt) {
      finishTerminal(session, "QUEUE_EXPIRED");
      return;
    }
    const retryAfterMs = session.retryAfterMs;
    const delay = Math.max(session.reconnectDelay, retryAfterMs);
    session.retryAfterMs = 0;
    if (now + delay >= session.ticketExpiresAt) {
      finishTerminal(session, "QUEUE_EXPIRED");
      return;
    }
    clientLog("matchmaking_reconnect_scheduled", {
      ticketId: session.ticketId,
      nextAttempt: session.connectionAttempt + 1,
      delayMs: delay,
      transportBackoffMs: session.reconnectDelay,
      retryAfterMs,
      ticketTimeRemainingMs: session.ticketExpiresAt - now,
      online: typeof navigator === "undefined" ? null : navigator.onLine,
      visibility: document.visibilityState
    });
    reconnectTimer.current = window.setTimeout(() => {
      reconnectTimer.current = null;
      if (sessionRef.current === session && !session.intentionalClose) connectRef.current(session);
    }, delay);
    session.reconnectDelay = Math.min(session.reconnectDelay * 2, 15_000);
  }, [finishTerminal]);

  const trackCancellation = useCallback((request: Promise<void>): Promise<void> => {
    const previous = pendingCancellationRef.current;
    let tracked: Promise<void>;
    tracked = Promise.all([previous, request]).then(() => undefined).finally(() => {
      if (pendingCancellationRef.current === tracked) pendingCancellationRef.current = null;
    });
    pendingCancellationRef.current = tracked;
    return tracked;
  }, []);

  const fallbackCancellation = useCallback((session: Session, failureEvent: string, keepalive = false): Promise<void> => {
    if (session.httpCancellation) return session.httpCancellation;
    session.httpCancellation = leaveMatchmaking(session.queueName, session.ticketId, keepalive).catch((error: unknown) => {
      clientError(failureEvent, {
        ticketId: session.ticketId,
        message: error instanceof Error ? error.message : "unknown"
      });
    });
    return session.httpCancellation;
  }, []);

  const settleCancellation = useCallback((session: Session, acknowledged: boolean, keepalive = false): void => {
    const pending = session.cancellation;
    if (!pending || pending.settled) return;
    pending.settled = true;
    if (pending.timer !== null) window.clearTimeout(pending.timer);
    pending.timer = null;
    const completion = acknowledged
      ? Promise.resolve()
      : fallbackCancellation(session, pending.failureEvent, keepalive);
    session.socket?.close(1000, acknowledged ? "Queue cancellation acknowledged" : "Queue cancellation fallback");
    void completion.finally(pending.resolve);
  }, [fallbackCancellation]);

  const beginCancellation = useCallback((
    session: Session,
    message: "QUEUE_CANCEL" | "OFFER_DECLINE",
    failureEvent: string,
    offerId?: string,
    teardown = false
  ): Promise<void> => {
    if (session.cancellation) return session.cancellation.promise;
    let resolve: () => void = () => {};
    const promise = new Promise<void>((done) => { resolve = done; });
    session.cancellation = { promise, resolve, timer: null, settled: false, failureEvent };
    session.intentionalClose = true;
    const socket = session.socket;
    if (socket?.readyState === WebSocket.OPEN) {
      socket.send(JSON.stringify({
        type: message,
        protocolVersion: PROTOCOL_VERSION,
        ticketId: session.ticketId,
        ...(offerId ? { offerId } : {})
      }));
      if (teardown) settleCancellation(session, false, true);
      else {
        session.cancellation.timer = window.setTimeout(() => {
          settleCancellation(session, false);
        }, CANCELLATION_ACK_TIMEOUT_MS);
      }
    } else {
      settleCancellation(session, false, teardown);
    }
    return trackCancellation(promise);
  }, [settleCancellation, trackCancellation]);

  const connect = useCallback((session: Session) => {
    if (sessionRef.current !== session || session.intentionalClose) return;
    const url = new URL(webSocketUrl("/api/matchmaking/socket"));
    url.searchParams.set("mode", session.serverMode);
    url.searchParams.set("region", session.region);
    url.searchParams.set("ticketId", session.ticketId);
    if (session.equippedSpecialItemId) url.searchParams.set("equippedSpecialItemId", session.equippedSpecialItemId);
    const attempt = ++session.connectionAttempt;
    const attemptId = `${session.ticketId}:${attempt}`;
    url.searchParams.set("attempt", String(attempt));
    url.searchParams.set("attemptId", attemptId);
    const startedAt = Date.now();
    let opened = false;
    let lastMessageType = "none";
    clientLog("matchmaking_socket_connecting", {
      ticketId: session.ticketId,
      attemptId,
      attempt,
      mode: session.mode,
      serverMode: session.serverMode,
      queueName: session.queueName,
      sessionAgeMs: startedAt - session.joinedAt,
      ticketTimeRemainingMs: session.ticketExpiresAt - startedAt,
      online: typeof navigator === "undefined" ? null : navigator.onLine,
      visibility: document.visibilityState
    });
    const socket = new WebSocket(url, MATCH_BINARY_SUBPROTOCOL);
    socket.binaryType = "arraybuffer";
    session.socket = socket;
    socketRef.current = socket;
    socket.addEventListener("open", () => {
      opened = true;
      clientLog("matchmaking_socket_opened", {
        ticketId: session.ticketId,
        attemptId,
        attempt,
        handshakeMs: Date.now() - startedAt,
        readyState: socket.readyState
      });
    });
    socket.addEventListener("message", (event) => {
      if (session.connectedMatchId) return;
      if (typeof event.data !== "string") {
        clientError("matchmaking_socket_invalid_message", {
          ticketId: session.ticketId, attemptId, attempt, reason: "non_string"
        });
        return;
      }
      let message: ServerMessage;
      try { message = JSON.parse(event.data) as ServerMessage; }
      catch {
        clientError("matchmaking_socket_invalid_message", {
          ticketId: session.ticketId, attemptId, attempt, reason: "invalid_json", payloadBytes: event.data.length
        });
        return;
      }
      lastMessageType = message.type ?? "unknown";
      clientLog("matchmaking_socket_message_received", {
        ticketId: session.ticketId,
        attemptId,
        attempt,
        messageType: lastMessageType,
        code: message.code,
        terminal: message.terminal,
        receivedAfterMs: Date.now() - startedAt,
        serverTransitMs: typeof message.serverTime === "number" ? Math.max(0, Date.now() - message.serverTime) : null
      });
      if (message.ticketId !== session.ticketId) {
        clientError("matchmaking_socket_ticket_mismatch", {
          ticketId: session.ticketId, receivedTicketId: message.ticketId ?? "missing", attemptId, attempt, messageType: lastMessageType
        });
        return;
      }
      if (message.type === "QUEUE_CANCELLED") {
        settleCancellation(session, true);
        return;
      }
      if (sessionRef.current !== session) return;
      if (typeof message.ticketExpiresAt === "number" && Number.isFinite(message.ticketExpiresAt)) {
        session.ticketExpiresAt = Math.min(session.ticketExpiresAt, message.ticketExpiresAt);
      }
      if (message.type === "QUEUE_ACCEPTED" || message.type === "QUEUE_WAITING") {
        session.reconnectDelay = 1_000;
        session.retryAfterMs = 0;
        session.queueName = message.queueName ?? session.queueName;
        session.joinedAt = message.joinedAt ?? session.joinedAt;
        callbackRef.current.onState(stateFor(session));
      } else if (message.type === "MATCH_OFFERED" && message.offerId && message.expiresAt) {
        session.reconnectDelay = 1_000;
        session.retryAfterMs = 0;
        setOffer({ offerId: message.offerId, ticketId: session.ticketId, expiresAt: message.expiresAt, region: message.region ?? session.region, opponentUsername: message.opponent?.username ?? "PLAYER", opponentXp: message.opponent?.xp ?? 0, opponentCosmetics: message.opponent?.cosmetics ?? { wallpaperId: "overworld-day", craftingTableId: "classic-brown", faceplateId: "trailblazer" }, opponentSpecialItemId: message.opponent?.equippedSpecialItemId ?? null });
      } else if (message.type === "MATCH_FOUND") finishMatch(session, message);
      else if (message.type === "OFFER_EXPIRED") setOffer((current) => current?.offerId === message.offerId ? null : current);
      else if (message.type === "QUEUE_EXPIRED") finishTerminal(session, "QUEUE_EXPIRED");
      else if (message.type === "QUEUE_ERROR") {
        clientLog("matchmaking_queue_error", {
          ticketId: session.ticketId,
          code: message.code ?? "QUEUE_ERROR",
          terminal: message.terminal !== false,
          retryAfterMs: message.retryAfterMs ?? 0
        });
        if (message.terminal !== false) finishTerminal(session, message.code ?? "QUEUE_ERROR");
        else {
          session.retryAfterMs = typeof message.retryAfterMs === "number" ? Math.max(0, message.retryAfterMs) : 0;
          socket.close(1013, "Retryable queue error");
        }
      }
    });
    socket.addEventListener("error", () => {
      clientError("matchmaking_socket_error", {
        ticketId: session.ticketId,
        attemptId,
        attempt,
        readyState: socket.readyState,
        phase: opened ? "open" : "handshake",
        elapsedMs: Date.now() - startedAt,
        lastMessageType,
        online: typeof navigator === "undefined" ? null : navigator.onLine,
        visibility: document.visibilityState
      });
    });
    socket.addEventListener("close", (event) => {
      clientLog("matchmaking_socket_closed", {
        ticketId: session.ticketId,
        attemptId,
        attempt,
        code: event.code,
        reason: event.reason || "none",
        clean: event.wasClean,
        phase: opened ? "open" : "handshake",
        lifetimeMs: Date.now() - startedAt,
        lastMessageType,
        online: typeof navigator === "undefined" ? null : navigator.onLine,
        visibility: document.visibilityState,
        intentional: session.intentionalClose,
        reconnectDelay: session.reconnectDelay,
        retryAfterMs: session.retryAfterMs,
        ticketTimeRemainingMs: Math.max(0, session.ticketExpiresAt - Date.now())
      });
      if (session.socket === socket) session.socket = null;
      if (socketRef.current === socket) socketRef.current = null;
      if (session.cancellation && !session.cancellation.settled) {
        settleCancellation(session, false);
        return;
      }
      if (!session.intentionalClose && !session.connectedMatchId) void recover(session);
    });
  }, [finishMatch, finishTerminal, recover, settleCancellation, stateFor]);
  connectRef.current = connect;

  const start = useCallback((mode: MatchMode, equippedSpecialItemId: string | null = null) => {
    const prior = sessionRef.current;
    let priorCancellation = pendingCancellationRef.current;
    if (prior) {
      priorCancellation = beginCancellation(prior, "QUEUE_CANCEL", "matchmaking_supersede_cancel_failed");
    }
    if (reconnectTimer.current !== null) window.clearTimeout(reconnectTimer.current);
    const session: Session = {
      mode,
      serverMode: mode === "quick" ? "casual" : "ranked",
      region: "auto",
      ticketId: crypto.randomUUID(),
      queueName: null,
      joinedAt: Date.now(),
      intentionalClose: false,
      connectedMatchId: null,
      connectionAttempt: 0,
      reconnectDelay: 1_000,
      retryAfterMs: 0,
      ticketExpiresAt: Date.now() + QUEUE_TICKET_TTL_MS,
      equippedSpecialItemId,
      socket: null,
      cancellation: null,
      httpCancellation: null
    };
    sessionRef.current = session;
    setOffer(null);
    callbackRef.current.onState(stateFor(session));
    if (priorCancellation) {
      void priorCancellation.finally(() => {
        if (sessionRef.current === session && !session.intentionalClose) connect(session);
      });
    } else {
      connect(session);
    }
    clientLog("matchmaking_socket_started", {
      mode,
      ticketId: session.ticketId,
      ticketExpiresAt: session.ticketExpiresAt,
      waitingForPriorCancellation: Boolean(priorCancellation)
    });
  }, [beginCancellation, connect, stateFor]);

  const acceptOffer = useCallback(() => {
    const session = sessionRef.current;
    const current = offer;
    const socket = session?.socket ?? null;
    if (!session || !current || !socket || socket.readyState !== WebSocket.OPEN) return;
    socket.send(JSON.stringify({ type: "OFFER_ACCEPT", protocolVersion: PROTOCOL_VERSION, ticketId: session.ticketId, offerId: current.offerId }));
    clientLog("match_offer_accepted", { offerId: current.offerId, ticketId: session.ticketId });
  }, [offer]);

  const declineOffer = useCallback(() => {
    const session = sessionRef.current;
    const current = offer;
    if (!session || !current) return;
    void beginCancellation(session, "OFFER_DECLINE", "matchmaking_cancel_failed", current.offerId);
    setOffer(null);
    callbackRef.current.onState(stateFor(session, "OFFER_DECLINED"));
  }, [beginCancellation, offer, stateFor]);

  const cancel = useCallback(() => {
    const session = sessionRef.current;
    if (!session) return;
    void beginCancellation(session, "QUEUE_CANCEL", "matchmaking_cancel_failed");
    sessionRef.current = null;
    setOffer(null);
    callbackRef.current.onState(null);
  }, [beginCancellation]);

  useEffect(() => {
    const visible = () => {
      const session = sessionRef.current;
      if (document.visibilityState === "visible" && session && !session.intentionalClose && (!session.socket || session.socket.readyState === WebSocket.CLOSED)) connectRef.current(session);
    };
    document.addEventListener("visibilitychange", visible);
    return () => {
      document.removeEventListener("visibilitychange", visible);
      if (reconnectTimer.current !== null) window.clearTimeout(reconnectTimer.current);
      const session = sessionRef.current;
      if (session) {
        void beginCancellation(session, "QUEUE_CANCEL", "matchmaking_unmount_cancel_failed", undefined, true);
      }
    };
  }, [beginCancellation]);

  return { start, cancel, offer, acceptOffer, declineOffer };
}
